import symbolMap from 'currency-symbol-map/map'
import { replace } from 'lodash'
import numeral from 'numeral'

// ----------------------------------------------------------------------

type CurrencyFormatOptions = {
  currency?: string
  decimalThreshold?: number
  minimumFractionDigits?: number
  maximumFractionDigits?: number
  locale?: string
}

function getCodeFromSymbol(symbol: string): string | undefined {
  const entry = Object.entries(symbolMap).find(([code, sym]) => sym === symbol)
  return entry ? entry[0] : undefined
}

function normalizeCurrencyInput(input?: string, fallback = 'USD'): string {
  if (!input) return fallback
  const upper = input.toUpperCase()

  // Already a valid ISO code
  if (symbolMap[upper]) return upper

  // Find ISO code by matching symbol
  return getCodeFromSymbol(input) || fallback
}

export function fCurrency(
  amount: number,
  {
    currency = 'USD',
    decimalThreshold = 1000,
    minimumFractionDigits,
    maximumFractionDigits,
    locale = 'en-US',
  }: CurrencyFormatOptions = {},
): string {
  const currencyCode = normalizeCurrencyInput(currency)

  const useDecimals = amount < decimalThreshold

  const formatOptions: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: minimumFractionDigits ?? (useDecimals ? 2 : 0),
    maximumFractionDigits: maximumFractionDigits ?? (useDecimals ? 2 : 0),
  }

  return new Intl.NumberFormat(locale, formatOptions).format(amount)
}

// Native values that cannot be normalized must never fall back to a USD symbol.
export function fCurrencyCode(
  amount: number,
  currency: string,
  {
    decimalThreshold = 1000,
    minimumFractionDigits,
    maximumFractionDigits,
    locale = 'en-US',
  }: Omit<CurrencyFormatOptions, 'currency'> = {},
): string {
  const useDecimals = Math.abs(amount) < decimalThreshold
  const formattedAmount = new Intl.NumberFormat(locale, {
    style: 'decimal',
    minimumFractionDigits: minimumFractionDigits ?? (useDecimals ? 2 : 0),
    maximumFractionDigits: maximumFractionDigits ?? (useDecimals ? 2 : 0),
  }).format(amount)

  return `${currency.toUpperCase()} ${formattedAmount}`
}

export function fCurrencyShort(amount: number, currency = 'USD'): string {
  if (isNaN(amount)) return '-'
  const symbol = symbolMap[currency] || '$'
  return `${symbol}${fShortenNumber(amount)}`
}

export function fPercent(number: number) {
  return numeral(number / 100).format('0.0%')
}

export function fNumber(number: number) {
  return numeral(number).format()
}

export function fShortenNumber(number: number) {
  return replace(numeral(number).format('0.00 a'), '.00', '')
}

export function fData(number: number) {
  return numeral(number).format('0.0 b')
}
